import Typography from "antd/lib/typography";
import React, { Fragment, useContext, useEffect } from "react";
import Helmet from "react-helmet";
import LayoutContext from "~context/Layout";

function PrivacyPage(): JSX.Element {
    const layout = useContext(LayoutContext);

    useEffect(() => {
        if (layout.data && layout.actions) {
            if (layout.data.header !== true) layout.actions.updateHeader(true);
            if (layout.data.footer !== true) layout.actions.updateFooter(true);
        }
    }, [layout.actions, layout.data]);

    return (
        <Fragment>
            <Helmet>
                <title>Privacy - Twenty4 Tan Co.</title>
            </Helmet>

            <div className="container-xl my-50 px-50">
                <Typography.Title level={1}>Privacy Policy</Typography.Title>
                <Typography.Paragraph>
                    Last updated: August 16, 2021
                </Typography.Paragraph>
                <Typography.Paragraph>
                    This Privacy Policy describes Our policies and procedures on
                    the collection, use and disclosure of Your information when
                    You use the Service and tells You about Your privacy rights
                    and how the law protects You.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    We use Your Personal data to provide and improve the
                    Service. By using the Service, You agree to the collection
                    and use of information in accordance with this Privacy
                    Policy. This Privacy Policy has been created with the help
                    of the{" "}
                    <Typography.Link
                        href="https://www.termsfeed.com/privacy-policy-generator/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Privacy Policy Generator
                    </Typography.Link>
                    .
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    Interpretation and Definitions
                </Typography.Title>
                <Typography.Title level={2}>Interpretation</Typography.Title>
                <Typography.Paragraph>
                    The words of which the initial letter is capitalized have
                    meanings defined under the following conditions. The
                    following definitions shall have the same meaning regardless
                    of whether they appear in singular or in plural.
                </Typography.Paragraph>
                <Typography.Title level={2}>Definitions</Typography.Title>
                <Typography.Paragraph>
                    For the purposes of this Privacy Policy:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Account
                            </Typography.Text>{" "}
                            means a unique account created for You to access our
                            Service or parts of our Service.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Company
                            </Typography.Text>{" "}
                            (referred to as either &quot;the Company&quot;,
                            &quot;We&quot;, &quot;Us&quot; or &quot;Our&quot; in
                            this Agreement) refers to Example LLC, 1234 Test St,
                            KS 66207.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Cookies
                            </Typography.Text>{" "}
                            are small files that are placed on Your computer,
                            mobile device or any other device by a website,
                            containing the details of Your browsing history on
                            that website among its many uses.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Country
                            </Typography.Text>{" "}
                            refers to: Kansas, United States
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Device
                            </Typography.Text>{" "}
                            means any device that can access the Service such as
                            a computer, a cellphone or a digital tablet.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Personal Data
                            </Typography.Text>{" "}
                            is any information that relates to an identified or
                            identifiable individual.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Service
                            </Typography.Text>{" "}
                            refers to the Website.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Service Provider
                            </Typography.Text>{" "}
                            means any natural or legal person who processes the
                            data on behalf of the Company. It refers to
                            third-party companies or individuals employed by the
                            Company to facilitate the Service, to provide the
                            Service on behalf of the Company, to perform
                            services related to the Service or to assist the
                            Company in analyzing how the Service is used.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Third-party Social Media Service
                            </Typography.Text>{" "}
                            refers to any website or any social network website
                            through which a User can log in or create an account
                            to use the Service.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Usage Data
                            </Typography.Text>{" "}
                            refers to data collected automatically, either
                            generated by the use of the Service or from the
                            Service infrastructure itself (for example, the
                            duration of a page visit).
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Website
                            </Typography.Text>{" "}
                            refers to Example Site, accessible from{" "}
                            <Typography.Link
                                href="https://twenty4tanco.com"
                                rel="external nofollow noopener noreferrer"
                                target="_blank"
                            >
                                https://twenty4tanco.com
                            </Typography.Link>
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>You</Typography.Text>{" "}
                            means the individual accessing or using the Service,
                            or the company, or other legal entity on behalf of
                            which such individual is accessing or using the
                            Service, as applicable.
                        </Typography.Paragraph>
                    </li>
                </ul>
                <Typography.Title level={1}>
                    Collecting and Using Your Personal Data
                </Typography.Title>
                <Typography.Title level={2}>
                    Types of Data Collected
                </Typography.Title>
                <Typography.Title level={3}>Personal Data</Typography.Title>
                <Typography.Paragraph>
                    While using Our Service, We may ask You to provide Us with
                    certain personally identifiable information that can be used
                    to contact or identify You. Personally identifiable
                    information may include, but is not limited to:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Paragraph>
                            Email address
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            First name and last name
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            Phone number
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>Usage Data</Typography.Paragraph>
                    </li>
                </ul>
                <Typography.Title level={3}>Usage Data</Typography.Title>
                <Typography.Paragraph>
                    Usage Data is collected automatically when using the
                    Service.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    Usage Data may include information such as Your
                    Device&apos;s Internet Protocol address (e.g. IP address),
                    browser type, browser version, the pages of our Service that
                    You visit, the time and date of Your visit, the time spent
                    on those pages, unique device identifiers and other
                    diagnostic data.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    When You access the Service by or through a mobile device,
                    We may collect certain information automatically, including,
                    but not limited to, the type of mobile device You use, Your
                    mobile device unique ID, the IP address of Your mobile
                    device, Your mobile operating system, the type of mobile
                    Internet browser You use, unique device identifiers and
                    other diagnostic data.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    We may also collect information that Your browser sends
                    whenever You visit our Service or when You access the
                    Service by or through a mobile device.
                </Typography.Paragraph>
                <Typography.Title level={3}>
                    Information from Third-Party Social Media Services
                </Typography.Title>
                <Typography.Paragraph>
                    The Company allows You to create an account and log in to
                    use the Service through the following Third-party Social
                    Media Services:
                </Typography.Paragraph>
                <ul>
                    <li>Google</li>
                </ul>
                <Typography.Paragraph>
                    If You decide to register through or otherwise grant us
                    access to a Third-Party Social Media Service, We may collect
                    Personal data that is already associated with Your
                    Third-Party Social Media Service&apos;s account, such as
                    Your name, Your email address, Your activities or Your
                    contact list associated with that account.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    You may also have the option of sharing additional
                    information with the Company through Your Third-Party Social
                    Media Service&apos;s account. If You choose to provide such
                    information and Personal Data, during registration or
                    otherwise, You are giving the Company permission to use,
                    share, and store it in a manner consistent with this Privacy
                    Policy.
                </Typography.Paragraph>
                <Typography.Title level={3}>
                    Tracking Technologies and Cookies
                </Typography.Title>
                <Typography.Paragraph>
                    We use Cookies and similar tracking technologies to track
                    the activity on Our Service and store certain information.
                    Tracking technologies used are beacons, tags, and scripts to
                    collect and track information and to improve and analyze Our
                    Service. The technologies We use may include:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Text strong={true}>
                            Cookies or Browser Cookies.
                        </Typography.Text>{" "}
                        A cookie is a small file placed on Your Device. You can
                        instruct Your browser to refuse all Cookies or to
                        indicate when a Cookie is being sent. However, if You do
                        not accept Cookies, You may not be able to use some
                        parts of our Service. Unless you have adjusted Your
                        browser setting so that it will refuse Cookies, our
                        Service may use Cookies.
                    </li>
                    <li>
                        <Typography.Text strong={true}>
                            Flash Cookies.
                        </Typography.Text>{" "}
                        Certain features of our Service may use local stored
                        objects (or Flash Cookies) to collect and store
                        information about Your preferences or Your activity on
                        our Service. Flash Cookies are not managed by the same
                        browser settings as those used for Browser Cookies. For
                        more information on how You can delete Flash Cookies,
                        please read &quot;Where can I change the settings for
                        disabling, or deleting local shared objects?&quot;
                        available at{" "}
                        <Typography.Link
                            href="https://helpx.adobe.com/flash-player/kb/disable-local-shared-objects-flash.html#main_Where_can_I_change_the_settings_for_disabling__or_deleting_local_shared_objects_"
                            rel="external nofollow noopener noreferrer"
                            target="_blank"
                        >
                            https://helpx.adobe.com/flash-player/kb/disable-local-shared-objects-flash.html#main_Where_can_I_change_the_settings_for_disabling__or_deleting_local_shared_objects_
                        </Typography.Link>
                    </li>
                    <li>
                        <Typography.Text strong={true}>
                            Web Beacons.
                        </Typography.Text>{" "}
                        Certain sections of our Service and our emails may
                        contain small electronic files known as web beacons
                        (also referred to as clear gifs, pixel tags, and
                        single-pixel gifs) that permit the Company, for example,
                        to count users who have visited those pages or opened an
                        email and for other related website statistics (for
                        example, recording the popularity of a certain section
                        and verifying system and server integrity).
                    </li>
                </ul>
                <Typography.Paragraph>
                    Cookies can be &quot;Persistent&quot; or &quot;Session&quot;
                    Cookies. Persistent Cookies remain on Your personal computer
                    or mobile device when You go offline, while Session Cookies
                    are deleted as soon as You close Your web browser. You can
                    learn more about cookies here:{" "}
                    <Typography.Link
                        href="https://www.termsfeed.com/blog/cookies/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        All About Cookies by TermsFeed
                    </Typography.Link>
                    .
                </Typography.Paragraph>
                <Typography.Paragraph>
                    We use both Session and Persistent Cookies for the purposes
                    set out below:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Necessary / Essential Cookies
                            </Typography.Text>
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Type: Session Cookies
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Administered by: Us
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Purpose: These Cookies are essential to provide You
                            with services available through the Website and to
                            enable You to use some of its features. They help to
                            authenticate users and prevent fraudulent use of
                            user accounts. Without these Cookies, the services
                            that You have asked for cannot be provided, and We
                            only use these Cookies to provide You with those
                            services.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Cookies Policy / Notice Acceptance Cookies
                            </Typography.Text>
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Type: Persistent Cookies
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Administered by: Us
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Purpose: These Cookies identify if users have
                            accepted the use of cookies on the Website.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                Functionality Cookies
                            </Typography.Text>
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Type: Persistent Cookies
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Administered by: Us
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            Purpose: These Cookies allow us to remember choices
                            You make when You use the Website, such as
                            remembering your login details or language
                            preference. The purpose of these Cookies is to
                            provide You with a more personal experience and to
                            avoid You having to re-enter your preferences every
                            time You use the Website.
                        </Typography.Paragraph>
                    </li>
                </ul>
                <Typography.Paragraph>
                    For more information about the cookies we use and your
                    choices regarding cookies, please visit our Cookies Policy
                    or the Cookies section of our Privacy Policy.
                </Typography.Paragraph>
                <Typography.Title level={2}>
                    Use of Your Personal Data
                </Typography.Title>
                <Typography.Paragraph>
                    The Company may use Personal Data for the following
                    purposes:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                To provide and maintain our Service
                            </Typography.Text>
                            , including to monitor the usage of our Service.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                To manage Your Account:
                            </Typography.Text>{" "}
                            to manage Your registration as a user of the
                            Service. The Personal Data You provide can give You
                            access to different functionalities of the Service
                            that are available to You as a registered user.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                For the performance of a contract:
                            </Typography.Text>{" "}
                            the development, compliance and undertaking of the
                            purchase contract for the products, items or
                            services You have purchased or of any other contract
                            with Us through the Service.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                To contact You:
                            </Typography.Text>{" "}
                            To contact You by email, telephone calls, SMS, or
                            other equivalent forms of electronic communication,
                            such as a mobile application&apos;s push
                            notifications regarding updates or informative
                            communications related to the functionalities,
                            products or contracted services, including the
                            security updates, when necessary or reasonable for
                            their implementation.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                To provide You
                            </Typography.Text>{" "}
                            with news, special offers and general information
                            about other goods, services and events which we
                            offer that are similar to those that you have
                            already purchased or enquired about unless You have
                            opted not to receive such information.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                To manage Your requests:
                            </Typography.Text>{" "}
                            To attend and manage Your requests to Us.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                For business transfers:
                            </Typography.Text>{" "}
                            We may use Your information to evaluate or conduct a
                            merger, divestiture, restructuring, reorganization,
                            dissolution, or other sale or transfer of some or
                            all of Our assets, whether as a going concern or as
                            part of bankruptcy, liquidation, or similar
                            proceeding, in which Personal Data held by Us about
                            our Service users is among the assets transferred.
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            <Typography.Text strong={true}>
                                For other purposes
                            </Typography.Text>
                            : We may use Your information for other purposes,
                            such as data analysis, identifying usage trends,
                            determining the effectiveness of our promotional
                            campaigns and to evaluate and improve our Service,
                            products, services, marketing and your experience.
                        </Typography.Paragraph>
                    </li>
                </ul>
                <Typography.Paragraph>
                    We may share Your personal information in the following
                    situations:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Text strong={true}>
                            With Service Providers:
                        </Typography.Text>{" "}
                        We may share Your personal information with Service
                        Providers to monitor and analyze the use of our Service,
                        to contact You.
                    </li>
                    <li>
                        <Typography.Text strong={true}>
                            For business transfers:
                        </Typography.Text>{" "}
                        We may share or transfer Your personal information in
                        connection with, or during negotiations of, any merger,
                        sale of Company assets, financing, or acquisition of all
                        or a portion of Our business to another company.
                    </li>
                    <li>
                        <Typography.Text strong={true}>
                            With Affiliates:
                        </Typography.Text>{" "}
                        We may share Your information with Our affiliates, in
                        which case we will require those affiliates to honor
                        this Privacy Policy. Affiliates include Our parent
                        company and any other subsidiaries, joint venture
                        partners or other companies that We control or that are
                        under common control with Us.
                    </li>
                    <li>
                        <Typography.Text strong={true}>
                            With business partners:
                        </Typography.Text>{" "}
                        We may share Your information with Our business partners
                        to offer You certain products, services or promotions.
                    </li>
                    <li>
                        <Typography.Text strong={true}>
                            With other users:
                        </Typography.Text>{" "}
                        when You share personal information or otherwise
                        interact in the public areas with other users, such
                        information may be viewed by all users and may be
                        publicly distributed outside. If You interact with other
                        users or register through a Third-Party Social Media
                        Service, Your contacts on the Third-Party Social Media
                        Service may see Your name, profile, pictures and
                        description of Your activity. Similarly, other users
                        will be able to view descriptions of Your activity,
                        communicate with You and view Your profile.
                    </li>
                    <li>
                        <Typography.Text strong={true}>
                            With Your consent
                        </Typography.Text>
                        : We may disclose Your personal information for any
                        other purpose with Your consent.
                    </li>
                </ul>
                <Typography.Title level={2}>
                    Retention of Your Personal Data
                </Typography.Title>
                <Typography.Paragraph>
                    The Company will retain Your Personal Data only for as long
                    as is necessary for the purposes set out in this Privacy
                    Policy. We will retain and use Your Personal Data to the
                    extent necessary to comply with our legal obligations (for
                    example, if we are required to retain your data to comply
                    with applicable laws), resolve disputes, and enforce our
                    legal agreements and policies.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    The Company will also retain Usage Data for internal
                    analysis purposes. Usage Data is generally retained for a
                    shorter period of time, except when this data is used to
                    strengthen the security or to improve the functionality of
                    Our Service, or We are legally obligated to retain this data
                    for longer time periods.
                </Typography.Paragraph>
                <Typography.Title level={2}>
                    Transfer of Your Personal Data
                </Typography.Title>
                <Typography.Paragraph>
                    Your information, including Personal Data, is processed at
                    the Company&apos;s operating offices and in any other places
                    where the parties involved in the processing are located. It
                    means that this information may be transferred to — and
                    maintained on — computers located outside of Your state,
                    province, country or other governmental jurisdiction where
                    the data protection laws may differ than those from Your
                    jurisdiction.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    Your consent to this Privacy Policy followed by Your
                    submission of such information represents Your agreement to
                    that transfer.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    The Company will take all steps reasonably necessary to
                    ensure that Your data is treated securely and in accordance
                    with this Privacy Policy and no transfer of Your Personal
                    Data will take place to an organization or a country unless
                    there are adequate controls in place including the security
                    of Your data and other personal information.
                </Typography.Paragraph>
                <Typography.Title level={2}>
                    Disclosure of Your Personal Data
                </Typography.Title>
                <Typography.Title level={3}>
                    Business Transactions
                </Typography.Title>
                <Typography.Paragraph>
                    If the Company is involved in a merger, acquisition or asset
                    sale, Your Personal Data may be transferred. We will provide
                    notice before Your Personal Data is transferred and becomes
                    subject to a different Privacy Policy.
                </Typography.Paragraph>
                <Typography.Title level={3}>Law enforcement</Typography.Title>
                <Typography.Paragraph>
                    Under certain circumstances, the Company may be required to
                    disclose Your Personal Data if required to do so by law or
                    in response to valid requests by public authorities (e.g. a
                    court or a government agency).
                </Typography.Paragraph>
                <Typography.Title level={3}>
                    Other legal requirements
                </Typography.Title>
                <Typography.Paragraph>
                    The Company may disclose Your Personal Data in the good
                    faith belief that such action is necessary to:
                </Typography.Paragraph>
                <ul>
                    <li>Comply with a legal obligation</li>
                    <li>
                        Protect and defend the rights or property of the Company
                    </li>
                    <li>
                        Prevent or investigate possible wrongdoing in connection
                        with the Service
                    </li>
                    <li>
                        Protect the personal safety of Users of the Service or
                        the public
                    </li>
                    <li>Protect against legal liability</li>
                </ul>
                <Typography.Title level={2}>
                    Security of Your Personal Data
                </Typography.Title>
                <Typography.Paragraph>
                    The security of Your Personal Data is important to Us, but
                    remember that no method of transmission over the Internet,
                    or method of electronic storage is 100% secure. While We
                    strive to use commercially acceptable means to protect Your
                    Personal Data, We cannot guarantee its absolute security.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    Children&apos;s Privacy
                </Typography.Title>
                <Typography.Paragraph>
                    Our Service does not address anyone under the age of 13. We
                    do not knowingly collect personally identifiable information
                    from anyone under the age of 13. If You are a parent or
                    guardian and You are aware that Your child has provided Us
                    with Personal Data, please contact Us. If We become aware
                    that We have collected Personal Data from anyone under the
                    age of 13 without verification of parental consent, We take
                    steps to remove that information from Our servers.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    If We need to rely on consent as a legal basis for
                    processing Your information and Your country requires
                    consent from a parent, We may require Your parent&apos;s
                    consent before We collect and use that information.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    Links to Other Websites
                </Typography.Title>
                <Typography.Paragraph>
                    Our Service may contain links to other websites that are not
                    operated by Us. If You click on a third party link, You will
                    be directed to that third party&apos;s site. We strongly
                    advise You to review the Privacy Policy of every site You
                    visit.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    We have no control over and assume no responsibility for the
                    content, privacy policies or practices of any third party
                    sites or services.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    Changes to this Privacy Policy
                </Typography.Title>
                <Typography.Paragraph>
                    We may update Our Privacy Policy from time to time. We will
                    notify You of any changes by posting the new Privacy Policy
                    on this page.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    We will let You know via email and/or a prominent notice on
                    Our Service, prior to the change becoming effective and
                    update the &quot;Last updated&quot; date at the top of this
                    Privacy Policy.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    You are advised to review this Privacy Policy periodically
                    for any changes. Changes to this Privacy Policy are
                    effective when they are posted on this page.
                </Typography.Paragraph>
                <Typography.Title level={1}>Contact Us</Typography.Title>
                <Typography.Paragraph>
                    If you have any questions about this Privacy Policy, You can
                    contact us:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Paragraph>
                            By email:{" "}
                            <Typography.Link
                                href="mailto:twenty4tanco@gmail.com"
                                rel="external nofollow noopener noreferrer"
                                target="_blank"
                            >
                                twenty4tanco@gmail.com
                            </Typography.Link>
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            By visiting this page on our website:{" "}
                            <Typography.Link
                                href="https://twenty4tanco.com/contact"
                                rel="external nofollow noopener noreferrer"
                                target="_blank"
                            >
                                https://twenty4tanco.com/contact
                            </Typography.Link>
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            By phone number:{" "}
                            <Typography.Link href="tel:+15736827146">
                                +1 (573) 682-7146
                            </Typography.Link>
                        </Typography.Paragraph>
                    </li>
                </ul>
            </div>
        </Fragment>
    );
}

export default PrivacyPage;
